// File: src/context/ToasterContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';

const ToasterContext = createContext();

export function useToaster() {
  return useContext(ToasterContext);
}

export function ToasterProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Add a new toast
  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();
    const lowerMsg = message.toLowerCase();

    setToasts((prev) => {
      // 1) If the new toast is about "ended", remove any "expired" toasts:
      if (lowerMsg.includes('ended the match') || lowerMsg.includes('ended the match early')) {
        // remove any existing 'expired' messages
        const filtered = prev.filter(
          (t) => !t.message.toLowerCase().includes('expired') 
        );
        // then add the new "ended" toast
        return [...filtered, { id, message, type, duration }];
      }

      // 2) If the new toast is about "expired", skip it if we already have 'ended'
      if (lowerMsg.includes('expired')) {
        // If there's already an "ended" toast in the queue, do nothing (skip)
        const alreadyEnded = prev.some((t) =>
          t.message.toLowerCase().includes('ended the match') ||
          t.message.toLowerCase().includes('ended the match early')
        );
        if (alreadyEnded) {
          return prev; // skip adding "expired"
        }
      }

      // Default: just add the toast
      return [...prev, { id, message, type, duration }];
    });

    return id;
  }, []);

  // Manually remove a toast
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Update an existing toast’s message
  const updateToast = useCallback((id, newMessage) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, message: newMessage } : t))
    );
  }, []);

  return (
    <ToasterContext.Provider value={{ toasts, addToast, removeToast, updateToast }}>
      {children}
    </ToasterContext.Provider>
  );
}
